import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  alpha,
  styled,
  Chip,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { useSnackbar } from '../../components/SnackbarProvider';
import {
  useTrashedOrganizations,
  useRestoreOrganization,
  usePermanentDeleteOrganization,
} from '../../hooks/useOrganizationDeletion';

const StyledCard = styled(Card)(() => ({
  background: 'linear-gradient(to bottom right, #ffffff, #fafbfc)',
  borderRadius: '16px',
  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  },
}));

const HeaderBox = styled(Box)(() => ({
  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
  padding: '48px 40px',
  borderRadius: '16px',
  marginBottom: '32px',
  boxShadow: '0 12px 40px rgba(239, 68, 68, 0.25)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      'radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.08) 0%, transparent 50%)',
  },
}));

const ActionButton = styled(IconButton)(() => ({
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.1)',
  },
}));

export const OrganizationTrash = () => {
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<{ id: string; name: string } | null>(null);
  const [confirmPermanentDelete, setConfirmPermanentDelete] = useState(false);

  const { data: trashedOrgs, isLoading } = useTrashedOrganizations();
  const restoreMutation = useRestoreOrganization();
  const permanentDeleteMutation = usePermanentDeleteOrganization();

  const handleRestoreClick = (id: string, name: string) => {
    setSelectedOrg({ id, name });
    setRestoreDialogOpen(true);
  };

  const handlePermanentDeleteClick = (id: string, name: string) => {
    setSelectedOrg({ id, name });
    setConfirmPermanentDelete(false);
    setPermanentDeleteDialogOpen(true);
  };

  const handleRestoreConfirm = () => {
    if (selectedOrg) {
      restoreMutation.mutate(selectedOrg.id, {
        onSuccess: () => {
          showMessage('Organization restored successfully', 'success');
          setRestoreDialogOpen(false);
          setSelectedOrg(null);
        },
        onError: (error: any) => {
          showMessage(error.response?.data?.message || 'Failed to restore organization', 'error');
        },
      });
    }
  };

  const handlePermanentDeleteConfirm = () => {
    if (selectedOrg) {
      permanentDeleteMutation.mutate(selectedOrg.id, {
        onSuccess: () => {
          showMessage('Organization permanently deleted', 'success');
          setPermanentDeleteDialogOpen(false);
          setSelectedOrg(null);
          setConfirmPermanentDelete(false);
        },
        onError: (error: any) => {
          showMessage(
            error.response?.data?.message || 'Failed to permanently delete organization',
            'error'
          );
        },
      });
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Organization',
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 600,
              fontSize: '14px',
              flexShrink: 0,
              opacity: 0.7,
            }}
          >
            {params.value?.charAt(0)?.toUpperCase() || 'O'}
          </Box>
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: '#697386',
                fontSize: '14px',
              }}
            >
              {params.value}
            </Typography>
            <Typography variant="caption" sx={{ color: '#9ca3af' }}>
              {params.row.slug}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'deletedAt',
      headerName: 'Deleted',
      width: 200,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" sx={{ color: '#697386', fontSize: '13px' }}>
            {formatDate(params.value)}
          </Typography>
          <Chip
            label="In Trash"
            size="small"
            sx={{
              mt: 0.5,
              bgcolor: alpha('#ef4444', 0.1),
              color: '#ef4444',
              fontSize: '11px',
              height: 20,
            }}
          />
        </Box>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Originally Created',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: '#9ca3af', fontSize: '13px' }}>
          {formatDate(params.value)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <ActionButton
            size="small"
            onClick={() => handleRestoreClick(params.row.id, params.row.name)}
            sx={{
              color: '#22c55e',
              '&:hover': {
                backgroundColor: alpha('#22c55e', 0.1),
              },
            }}
            title="Restore organization"
          >
            <RestoreIcon fontSize="small" />
          </ActionButton>
          <ActionButton
            size="small"
            onClick={() => handlePermanentDeleteClick(params.row.id, params.row.name)}
            sx={{
              color: '#ef4444',
              '&:hover': {
                backgroundColor: alpha('#ef4444', 0.1),
              },
            }}
            title="Permanently delete"
          >
            <DeleteForeverIcon fontSize="small" />
          </ActionButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ pb: 4 }}>
      <HeaderBox>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <DeleteSweepIcon sx={{ fontSize: 40, color: '#fff', opacity: 0.9 }} />
            <Typography
              variant="h3"
              sx={{
                color: '#fff',
                fontWeight: 700,
                fontSize: '36px',
                letterSpacing: '-0.02em',
              }}
            >
              Trash
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255, 255, 255, 0.85)',
              fontSize: '15px',
              maxWidth: '600px',
            }}
          >
            Deleted organizations can be restored or permanently removed
          </Typography>
        </Box>
      </HeaderBox>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/organizations')}
          sx={{
            borderRadius: '10px',
            padding: '10px 24px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '14px',
            borderColor: '#697386',
            color: '#697386',
            '&:hover': {
              borderColor: '#0891b2',
              color: '#0891b2',
              backgroundColor: alpha('#0891b2', 0.05),
            },
          }}
        >
          Back to Organizations
        </Button>
      </Box>

      {trashedOrgs && trashedOrgs.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          <Typography variant="body2">
            No deleted organizations. When you delete an organization, it will appear here.
          </Typography>
        </Alert>
      ) : (
        <StyledCard>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <DataGrid
              rows={trashedOrgs || []}
              columns={columns}
              loading={isLoading}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10 },
                },
              }}
              autoHeight
              disableRowSelectionOnClick
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#fafbfc',
                  borderBottom: '2px solid #e3e8ef',
                  borderRadius: 0,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 700,
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: '#697386',
                },
                '& .MuiDataGrid-row': {
                  borderBottom: '1px solid #e3e8ef',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: alpha('#ef4444', 0.03),
                  },
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: 'none',
                  padding: '16px',
                },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: '2px solid #e3e8ef',
                  backgroundColor: '#fafbfc',
                },
              }}
            />
          </CardContent>
        </StyledCard>
      )}

      {/* Restore Dialog */}
      <Dialog
        open={restoreDialogOpen}
        onClose={() => setRestoreDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            padding: '8px',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '20px', color: '#1a1f36' }}>
          Restore Organization
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#697386' }}>
            Are you sure you want to restore <strong>{selectedOrg?.name}</strong>? This will restore
            the organization and all its associated data (users, products, sales, etc.).
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={() => setRestoreDialogOpen(false)}
            sx={{
              textTransform: 'none',
              color: '#697386',
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRestoreConfirm}
            disabled={restoreMutation.isPending}
            sx={{
              textTransform: 'none',
              backgroundColor: '#22c55e',
              color: '#fff',
              fontWeight: 600,
              borderRadius: '8px',
              padding: '8px 20px',
              '&:hover': {
                backgroundColor: '#16a34a',
              },
            }}
          >
            {restoreMutation.isPending ? 'Restoring...' : 'Restore'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permanent Delete Dialog */}
      <Dialog
        open={permanentDeleteDialogOpen}
        onClose={() => setPermanentDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            padding: '8px',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '20px', color: '#ef4444' }}>
          Permanently Delete Organization
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              This action is IRREVERSIBLE!
            </Typography>
          </Alert>
          <DialogContentText sx={{ color: '#697386', mb: 2 }}>
            You are about to permanently delete <strong>{selectedOrg?.name}</strong>. This will
            remove all organization data including:
          </DialogContentText>
          <Box component="ul" sx={{ color: '#697386', pl: 2 }}>
            <li>All users and their accounts</li>
            <li>All products and inventory</li>
            <li>All sales and invoices</li>
            <li>All quotes and B2B data</li>
            <li>All other associated records</li>
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmPermanentDelete}
                onChange={(e) => setConfirmPermanentDelete(e.target.checked)}
                color="error"
              />
            }
            label={
              <Typography variant="body2" color="error">
                I understand this action cannot be undone
              </Typography>
            }
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={() => setPermanentDeleteDialogOpen(false)}
            sx={{
              textTransform: 'none',
              color: '#697386',
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePermanentDeleteConfirm}
            disabled={permanentDeleteMutation.isPending || !confirmPermanentDelete}
            sx={{
              textTransform: 'none',
              backgroundColor: '#ef4444',
              color: '#fff',
              fontWeight: 600,
              borderRadius: '8px',
              padding: '8px 20px',
              '&:hover': {
                backgroundColor: '#dc2626',
              },
              '&:disabled': {
                backgroundColor: '#fca5a5',
                color: '#fff',
              },
            }}
          >
            {permanentDeleteMutation.isPending ? 'Deleting...' : 'Permanently Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
